/**
 *  PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2026 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */
/**
 * @file middlewareUtils.js - Helpers for normalizing, building, and formatting agent middleware payloads.
 */

import languages from '../../../common/languages/en';
import {
  PII_FILTER_ACTION_LABEL_KEY,
  PII_FILTER_ACTION_OPTIONS,
  PII_FILTER_DEFAULT_ACTION,
  PII_FILTER_ICON_TYPE,
  PII_FILTER_MIDDLEWARE_KEY
} from '../constants/middlewareConstants';

const PII_FILTER_HISTORY_LABEL_KEY = 'editNodeModal.PII_FILTER_HISTORY_LABEL';
const PII_FILTER_HISTORY_NO_FIELDS_KEY = 'editNodeModal.PII_FILTER_HISTORY_NO_FIELDS';
const MIDDLEWARE_PLUGIN_CONFIGURED_KEY = 'editNodeModal.MIDDLEWARE_PLUGIN_CONFIGURED';

export const defaultPiiFilterMessageResolver = (key) => languages[key] || key;

export const createDefaultPiiFilterFormState = () => ({
  piiFilterEnabled: false,
  piiFilterFields: {},
  preservedUnknownPiiFilterFields: {},
  otherMiddlewarePlugins: {}
});

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const formatFilterDisplayName = (filterName = '') =>
  filterName
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const normalizePiiFilterConfigs = (piiFilter) => {
  if (!Array.isArray(piiFilter)) {
    return [];
  }

  return piiFilter
    .filter((entry) => entry?.filterName && Array.isArray(entry.filterStrategy) && entry.filterStrategy.length > 0)
    .map(({ filterName, filterStrategy }) => ({
      filterName,
      filterStrategy: filterStrategy.filter((strategy) => typeof strategy === 'string' && strategy)
    }))
    .filter(({ filterStrategy }) => filterStrategy.length > 0);
};

export const getDefaultFilterAction = (filterStrategy = []) => {
  const strategy = filterStrategy.find((value) => PII_FILTER_ACTION_OPTIONS.includes(value));
  return strategy || filterStrategy[0] || PII_FILTER_DEFAULT_ACTION;
};

export const isValidActionForFilter = (action, filterStrategy = []) =>
  typeof action === 'string' && filterStrategy.includes(action);

export const getSelectedFilterAction = (filterName, filterStrategy = [], piiFilterFields = {}) => {
  const savedAction = piiFilterFields[filterName]?.action;
  return isValidActionForFilter(savedAction, filterStrategy) ? savedAction : getDefaultFilterAction(filterStrategy);
};

export const buildPiiFilterFieldsFromConfig = (availableFilters = [], existingFields = {}, existingPreserved = {}) => {
  const availableNames = new Set(availableFilters.map(({ filterName }) => filterName));
  const piiFilterFields = {};
  const preservedUnknownPiiFilterFields = { ...existingPreserved };

  availableFilters.forEach(({ filterName, filterStrategy }) => {
    piiFilterFields[filterName] = {
      action: getSelectedFilterAction(filterName, filterStrategy, existingFields)
    };
    delete preservedUnknownPiiFilterFields[filterName];
  });

  Object.entries(existingFields).forEach(([key, { action }]) => {
    if (!availableNames.has(key) && action) {
      preservedUnknownPiiFilterFields[key] = action;
    }
  });

  return { piiFilterFields, preservedUnknownPiiFilterFields };
};

/**
 * Normalizes middleware from either the object format or the legacy array format.
 */
export const normalizeMiddlewareInput = (middleware) => {
  const defaultResult = {
    otherMiddlewarePlugins: {},
    mergedPiiFilter: {},
    hasPiiFilterEntry: false
  };

  if (!middleware) {
    return defaultResult;
  }

  if (Array.isArray(middleware)) {
    const otherMiddlewarePlugins = {};
    const mergedPiiFilter = {};
    let hasPiiFilterEntry = false;

    middleware.forEach((entry) => {
      if (!isPlainObject(entry)) {
        return;
      }

      if (Object.prototype.hasOwnProperty.call(entry, PII_FILTER_MIDDLEWARE_KEY)) {
        hasPiiFilterEntry = true;
        const piiFilter = entry[PII_FILTER_MIDDLEWARE_KEY];
        if (isPlainObject(piiFilter)) {
          Object.assign(mergedPiiFilter, piiFilter);
        }
        return;
      }

      Object.entries(entry).forEach(([key, value]) => {
        otherMiddlewarePlugins[key] = value;
      });
    });

    return { otherMiddlewarePlugins, mergedPiiFilter, hasPiiFilterEntry };
  }

  if (isPlainObject(middleware)) {
    const { [PII_FILTER_MIDDLEWARE_KEY]: piiFilter, ...otherMiddlewarePlugins } = middleware;
    const hasPiiFilterEntry = Object.prototype.hasOwnProperty.call(middleware, PII_FILTER_MIDDLEWARE_KEY);
    const mergedPiiFilter = isPlainObject(piiFilter) ? { ...piiFilter } : {};

    return {
      otherMiddlewarePlugins,
      mergedPiiFilter,
      hasPiiFilterEntry
    };
  }

  return defaultResult;
};

export const normalizeMiddlewareForStorage = (middleware) => {
  const { otherMiddlewarePlugins, mergedPiiFilter, hasPiiFilterEntry } = normalizeMiddlewareInput(middleware);
  const normalized = { ...otherMiddlewarePlugins };

  if (hasPiiFilterEntry) {
    normalized[PII_FILTER_MIDDLEWARE_KEY] = mergedPiiFilter;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

export const getPiiFilterHistoryActionLabel = (action, getMessage = defaultPiiFilterMessageResolver) => {
  const labelKey = PII_FILTER_ACTION_LABEL_KEY[action];
  return labelKey ? getMessage(labelKey) : action;
};

export const getPiiFilterActionDisplayLabel = (action, getMessage = defaultPiiFilterMessageResolver) =>
  getPiiFilterHistoryActionLabel(action, getMessage);

export const buildPiiFilterHistoryPropertyNameMap = (getMessage = defaultPiiFilterMessageResolver) =>
  Object.fromEntries(
    PII_FILTER_ACTION_OPTIONS.map((action) => [action, getPiiFilterHistoryActionLabel(action, getMessage)])
  );

export const parsePiiFilterFormStateFromMiddleware = (middleware) => {
  const defaultState = createDefaultPiiFilterFormState();
  const { otherMiddlewarePlugins, mergedPiiFilter, hasPiiFilterEntry } = normalizeMiddlewareInput(middleware);

  if (!hasPiiFilterEntry) {
    return {
      ...defaultState,
      otherMiddlewarePlugins
    };
  }

  const piiFilterFields = {};

  Object.entries(mergedPiiFilter).forEach(([key, action]) => {
    if (typeof action === 'string' && action) {
      piiFilterFields[key] = { action };
    }
  });

  return {
    piiFilterEnabled: true,
    piiFilterFields,
    preservedUnknownPiiFilterFields: {},
    otherMiddlewarePlugins
  };
};

/**
 * Builds middleware payload from form state as a single object, e.g. `{ piiFilter: { ... } }`.
 */
export const buildMiddlewarePayloadFromPiiFilterState = ({
  piiFilterEnabled,
  piiFilterFields,
  preservedUnknownPiiFilterFields = {},
  otherMiddlewarePlugins = {}
}) => {
  const middleware = { ...(isPlainObject(otherMiddlewarePlugins) ? otherMiddlewarePlugins : {}) };

  if (piiFilterEnabled) {
    const piiFilter = { ...(isPlainObject(preservedUnknownPiiFilterFields) ? preservedUnknownPiiFilterFields : {}) };

    Object.entries(piiFilterFields || {}).forEach(([key, { action }]) => {
      if (action) {
        piiFilter[key] = action;
      }
    });

    middleware[PII_FILTER_MIDDLEWARE_KEY] = piiFilter;
  } else if (Object.keys(preservedUnknownPiiFilterFields).length > 0) {
    middleware[PII_FILTER_MIDDLEWARE_KEY] = { ...preservedUnknownPiiFilterFields };
  }

  return Object.keys(middleware).length > 0 ? middleware : {};
};

export const formatPiiFilterMiddlewareSummary = (piiFilter = {}, getMessage = defaultPiiFilterMessageResolver) => {
  const parts = Object.entries(piiFilter).map(([key, action]) => {
    const fieldLabel = formatFilterDisplayName(key);
    const actionLabel = getPiiFilterHistoryActionLabel(action, getMessage) || action;
    return `${fieldLabel}: ${actionLabel}`;
  });

  const fields = parts.join(', ');

  return fields
    ? `${getMessage(PII_FILTER_HISTORY_LABEL_KEY)} (${fields})`
    : getMessage(PII_FILTER_HISTORY_NO_FIELDS_KEY);
};

export const formatMiddlewareSummary = (middleware, getMessage = defaultPiiFilterMessageResolver) => {
  const { otherMiddlewarePlugins, mergedPiiFilter, hasPiiFilterEntry } = normalizeMiddlewareInput(middleware);
  const parts = [];

  if (hasPiiFilterEntry) {
    parts.push(formatPiiFilterMiddlewareSummary(mergedPiiFilter, getMessage));
  }

  Object.keys(otherMiddlewarePlugins).forEach((pluginKey) => {
    const template = getMessage(MIDDLEWARE_PLUGIN_CONFIGURED_KEY);
    parts.push(template.replace('{pluginKey}', pluginKey));
  });

  return parts.filter(Boolean).join(' | ');
};

/**
 * Resolves agent middleware for EditNodeModal save.
 */
export const resolveAgentMiddlewareOnSave = ({ isAgent, buildMiddlewarePayload }) => {
  if (isAgent) {
    return buildMiddlewarePayload();
  }

  return undefined;
};

export const getPiiFilterIconTypeForFilterName = (filterName = '') => {
  const normalized = filterName.toLowerCase().replace(/[\s_-]/g, '');

  if (normalized === 'email') {
    return PII_FILTER_ICON_TYPE.EMAIL;
  }
  if (normalized === 'creditcard') {
    return PII_FILTER_ICON_TYPE.CREDIT_CARD;
  }
  if (normalized === 'firstname' || normalized === 'lastname') {
    return PII_FILTER_ICON_TYPE.PERSON;
  }

  return null;
};
